import React, { useState, useEffect, useCallback } from "react";
import {
    Button,
    Col,
    Form,
    Image,
    OverlayTrigger,
    Row,
    Tooltip,
} from "react-bootstrap";
import infoImg from "../../../../../../assets/infoimg.png";
import { countriesList } from "../../../../../../store/AllListData";
import { FaEye, FaTrashAlt } from "react-icons/fa";

const DetailsOfDispute = ({ formData, setFormData, showValidation }) => {
    const [fieldErrors, setFieldErrors] = useState({});
    const [uploadedFile, setUploadedFile] = useState(null);
    const [uploadError, setUploadError] = useState("");
    const [isFormValid, setIsFormValid] = useState(false);

    const validateField = useCallback((name, value) => {
        let error = "";
        switch (name) {
            case "agreementStartDate":
            case "disputeStartDate":
                if (!value) error = "Date is required";
                break;
            case "commercialInvoiceAmount":
            case "amountOfActivity":
            case "amountInDispute":
                if (!value) error = "Amount is required";
                break;
            case "quantumOfClaim":
                if (!value) error = "Quantum of claim is required";
                break;
            case "disputeDescription":
                if (!value) error = "Dispute description is required";
                else if (value.length < 50)
                    error = "Description must be at least 50 characters";
                break;
            case "respondentCompanyName":
            case "respondentRepresentativeName":
                if (!value) error = "This field is required";
                else if (value.length < 3) error = "Must be at least 3 characters";
                break;
            case "respondentAddress":
                if (!value) error = "Address is required";
                else if (value.length < 10)
                    error = "Address must be at least 10 characters";
                break;
            case "respondentEmail":
                if (!value) error = "Email is required";
                break;
            case "respondentCountry":
                if (!value) error = "Country is required";
                break;
            case "respondentState":
                if (!value) error = "State is required";
                else if (value.length < 2) error = "State must be at least 2 characters";
                break;
            case "respondentPanNo":
                if (value && !/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(value))
                    error = "Invalid PAN number format (e.g., ABCDE1234F)";
                break;
            case "respondentZipCode":
            case "respondentWebsite":
                error = "";
                break;
            default:
                if (!value) error = "This field is required";
                break;
        }
        return error;
    }, []);
    
    const handleInputChange = (e) => {
        const { name, value } = e.target;
        let formattedValue = value;
        if (name === "respondentPanNo") {
            formattedValue = value.toUpperCase();
        }
        setFormData((prev) => ({
            ...prev,
            [name]: formattedValue,
        }));

        const error = validateField(name, formattedValue);
        setFieldErrors((prev) => ({
            ...prev,
            [name]: error,
        }));
    };
    // Initialize uploadedFile from formData when component mounts
    useEffect(() => {
        if (formData.agreementContract) {
            setUploadedFile(formData.agreementContract);
        }
    }, [formData.agreementContract]);

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        setUploadError("");

        if (!file) return;

        const allowedTypes = [
            "application/pdf",
            "image/jpeg",
            "image/jpg",
            "image/png",
            "application/msword",
            "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
        ];

        if (!allowedTypes.includes(file.type)) {
            setUploadError("Only PDF, images (JPG, PNG) and Word documents are allowed");
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setUploadError("File size should not exceed 5MB");
            return;
        }

        setUploadedFile(file);
        setFormData((prev) => ({
            ...prev,
            agreementContract: file,
        }));
    };

    const handleFileDelete = () => {
        setUploadedFile(null);
        setFormData((prev) => ({
            ...prev,
            agreementContract: null,
        }));
    };

    // Always keep representative name in sync with respondent name from previous step
    useEffect(() => {
        if (formData.respondentName !== formData.respondentRepresentativeName) {
            setFormData((prev) => ({
                ...prev,
                respondentRepresentativeName: formData.respondentName || "",
            }));
        }
    }, [formData.respondentRepresentativeName, formData.respondentName, setFormData]);

    useEffect(() => {
        if (showValidation) {
            const fields = [
                "agreementStartDate",
                "product",
                "commercialInvoiceAmount",
                "disputeStartDate",
                "amountOfActivity",
                "quantumOfClaim",
                "amountInDispute",
                "disputeDescription",
                "respondentCompanyName",
                "respondentRepresentativeName",
                "respondentEmail",
                "respondentAddress",
                "respondentCountry",
                "respondentState",
                "respondentZipCode",
                "respondentPanNo",
                "respondentWebsite",
            ];

            const newErrors = {};
            fields.forEach((field) => {
                newErrors[field] = validateField(field, formData[field]);
            });

            setFieldErrors(newErrors);
        }
    }, [showValidation, formData, validateField]);

    // Check if all required fields are filled
    useEffect(() => {
        const requiredFields = [
            "agreementStartDate",
            "product",
            "commercialInvoiceAmount",
            "disputeStartDate",
            "amountOfActivity",
            "quantumOfClaim",
            "amountInDispute",
            "disputeDescription",
            "respondentCompanyName",
            "respondentEmail",
            "respondentRepresentativeName",
            "respondentAddress",
            "respondentCountry",
            "respondentState",
            "termsAccepted",
        ];

        const isAllFieldsFilled = requiredFields.every((field) => {
            if (field === "termsAccepted") {
                return formData[field] === true;
            }
            return formData[field] && formData[field].trim() !== "";
        });

        const isFileUploaded = uploadedFile !== null;

        setIsFormValid(isAllFieldsFilled && isFileUploaded);
    }, [formData, uploadedFile]);

    return (
        <>
            <div>
                <Row className="mb-3 mt-3 justify-content-center align-items-center">
                    <Col lg="6">
                        <h5 className="">Agreement/contract <span className="text-danger">*</span></h5>
                    </Col>
                    <Col lg="6">
                        <Form.Group>
                            <Form.Control
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                                onChange={handleFileUpload}
                                style={{ display: "none" }}
                                id="agreement-contract-upload"
                            />
                            <div className="mt-2 d-flex align-items-center">
                                <Button
                                    variant="success"
                                    size="sm"
                                    onClick={() => document.getElementById("agreement-contract-upload").click()}
                                    className="me-2"
                                >
                                    {uploadedFile ? "Change File" : "Upload"}
                                </Button>
                                {uploadedFile && (
                                    <>
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => {
                                                const fileURL = URL.createObjectURL(uploadedFile);
                                                window.open(fileURL, "_blank");
                                            }}
                                            title="View uploaded file"
                                        >
                                            <FaEye />
                                        </Button>
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={handleFileDelete}
                                            title="Delete uploaded file"
                                            className="ms-2"
                                        >
                                            <FaTrashAlt />
                                        </Button>
                                    </>
                                )}
                            </div>
                            {uploadError && (
                                <div className="mt-2">
                                    <small className="text-danger">{uploadError}</small>
                                </div>
                            )}
                            {showValidation && !uploadedFile && (
                                <div className="mt-2">
                                    <small className="text-danger">Agreement/contract document is required</small>
                                </div>
                            )}
                            <div className="mt-2">
                                <small className="text-danger">Max 5MB (PDF, JPG, PNG, DOC, DOCX)</small>
                            </div>
                        </Form.Group>
                    </Col>
                </Row>

                <Form.Group controlId="agreementStartDate">
                    <Row>
                        <Col lg="6">
                            <Form.Label className="fw-semibold fs-6 mb-1">
                                Agreement Start Date <span className="text-danger">*</span>
                            </Form.Label>
                        </Col>
                        <Col lg="6">
                            <Form.Control
                                type="date"
                                name="agreementStartDate"
                                onChange={handleInputChange}
                                value={formData.agreementStartDate}
                                required
                                isInvalid={showValidation && !!fieldErrors.agreementStartDate}
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.agreementStartDate}
                            </Form.Control.Feedback>
                        </Col>
                    </Row>
                </Form.Group>

                <Row className="mb-3">
                    <Form.Group as={Col} controlId="product">
                        <Form.Label className="fw-semibold fs-6 mb-1">
                            Product <span className="text-danger">*</span>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="button-tooltip-2">Enter the product name</Tooltip>}
                            >
                                {({ ref, ...triggerHandler }) => (
                                    <Button
                                        variant="light"
                                        {...triggerHandler}
                                        className="d-inline-flex align-items-center"
                                        style={{ backgroundColor: "transparent", border: "none" }}
                                    >
                                        <Image
                                            ref={ref}
                                            roundedCircle
                                            src={infoImg}
                                            style={{ height: "3vh" }}
                                            alt=""
                                        />
                                    </Button>
                                )}
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            onChange={handleInputChange}
                            value={formData.product}
                            name="product"
                            maxLength={100}
                            required
                            placeholder="Enter product name"
                            isInvalid={showValidation && !!fieldErrors.product}
                        />
                        <Form.Control.Feedback type="invalid">
                            {fieldErrors.product}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group as={Col} controlId="commercialInvoiceAmount">
                        <Form.Label className="fw-semibold fs-6 mb-1">
                            Commercial invoice amount <span className="text-danger">*</span>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="button-tooltip-2">Enter the amount</Tooltip>}
                            >
                                {({ ref, ...triggerHandler }) => (
                                    <Button
                                        variant="light"
                                        {...triggerHandler}
                                        className="d-inline-flex align-items-center"
                                        style={{ backgroundColor: "transparent", border: "none" }}
                                    >
                                        <Image
                                            ref={ref}
                                            roundedCircle
                                            src={infoImg}
                                            style={{ height: "3vh" }}
                                            alt=""
                                        />
                                    </Button>
                                )}
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, "");
                                handleInputChange({ target: { name: "commercialInvoiceAmount", value } });
                            }}
                            value={formData.commercialInvoiceAmount}
                            name="commercialInvoiceAmount"
                            maxLength={20}
                            required
                            placeholder="Enter amount"
                            isInvalid={showValidation && !!fieldErrors.commercialInvoiceAmount}
                        />
                        <Form.Control.Feedback type="invalid">
                            {fieldErrors.commercialInvoiceAmount}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Row>

                <Row className="mb-3">
                    <Form.Group as={Col} controlId="disputeStartDate">
                        <Form.Label className="fw-semibold fs-6 mb-1">
                            Dispute Start Date <span className="text-danger">*</span>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="button-tooltip-2">Select the date</Tooltip>}
                            >
                                {({ ref, ...triggerHandler }) => (
                                    <Button
                                        variant="light"
                                        {...triggerHandler}
                                        className="d-inline-flex align-items-center"
                                        style={{ backgroundColor: "transparent", border: "none" }}
                                    >
                                        <Image
                                            ref={ref}
                                            roundedCircle
                                            src={infoImg}
                                            style={{ height: "3vh" }}
                                            alt=""
                                        />
                                    </Button>
                                )}
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Control
                            type="date"
                            onChange={handleInputChange}
                            value={formData.disputeStartDate}
                            name="disputeStartDate"
                            required
                            isInvalid={showValidation && !!fieldErrors.disputeStartDate}
                        />
                        <Form.Control.Feedback type="invalid">
                            {fieldErrors.disputeStartDate}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group as={Col} controlId="amountOfActivity">
                        <Form.Label className="fw-semibold fs-6 mb-1">
                            Amount of Activity <span className="text-danger">*</span>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="button-tooltip-2">Enter the amount</Tooltip>}
                            >
                                {({ ref, ...triggerHandler }) => (
                                    <Button
                                        variant="light"
                                        {...triggerHandler}
                                        className="d-inline-flex align-items-center"
                                        style={{ backgroundColor: "transparent", border: "none" }}
                                    >
                                        <Image
                                            ref={ref}
                                            roundedCircle
                                            src={infoImg}
                                            style={{ height: "3vh" }}
                                            alt=""
                                        />
                                    </Button>
                                )}
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, "");
                                handleInputChange({ target: { name: "amountOfActivity", value } });
                            }}
                            value={formData.amountOfActivity}
                            name="amountOfActivity"
                            maxLength={20}
                            required
                            placeholder="Enter amount"
                            isInvalid={showValidation && !!fieldErrors.amountOfActivity}
                        />
                        <Form.Control.Feedback type="invalid">
                            {fieldErrors.amountOfActivity}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Row>

                <Row className="mb-3">
                    <Form.Group as={Col} controlId="quantumOfClaim">
                        <Form.Label className="fw-semibold fs-6 mb-1">
                            Quantum of Claim (in USD) <span className="text-danger">*</span>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="button-tooltip-2">Enter the amount</Tooltip>}
                            >
                                {({ ref, ...triggerHandler }) => (
                                    <Button
                                        variant="light"
                                        {...triggerHandler}
                                        className="d-inline-flex align-items-center"
                                        style={{ backgroundColor: "transparent", border: "none" }}
                                    >
                                        <Image
                                            ref={ref}
                                            roundedCircle
                                            src={infoImg}
                                            style={{ height: "3vh" }}
                                            alt=""
                                        />
                                    </Button>
                                )}
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, "");
                                handleInputChange({ target: { name: "quantumOfClaim", value } });
                            }}
                            value={formData.quantumOfClaim}
                            name="quantumOfClaim"
                            maxLength={20}
                            required
                            placeholder="Enter amount"
                            isInvalid={showValidation && !!fieldErrors.quantumOfClaim}
                        />
                        <Form.Control.Feedback type="invalid">
                            {fieldErrors.quantumOfClaim}
                        </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group as={Col} controlId="amountInDispute">
                        <Form.Label className="fw-semibold fs-6 mb-1">
                            Amount in Dispute (in USD) <span className="text-danger">*</span>
                            <OverlayTrigger
                                placement="right"
                                overlay={<Tooltip id="button-tooltip-2">Enter the amount</Tooltip>}
                            >
                                {({ ref, ...triggerHandler }) => (
                                    <Button
                                        variant="light"
                                        {...triggerHandler}
                                        className="d-inline-flex align-items-center"
                                        style={{ backgroundColor: "transparent", border: "none" }}
                                    >
                                        <Image
                                            ref={ref}
                                            roundedCircle
                                            src={infoImg}
                                            style={{ height: "3vh" }}
                                            alt=""
                                        />
                                    </Button>
                                )}
                            </OverlayTrigger>
                        </Form.Label>
                        <Form.Control
                            type="text"
                            onChange={(e) => {
                                const value = e.target.value.replace(/[^0-9]/g, "");
                                handleInputChange({ target: { name: "amountInDispute", value } });
                            }}
                            value={formData.amountInDispute}
                            name="amountInDispute"
                            maxLength={20}
                            required
                            placeholder="Enter amount in USD"
                            isInvalid={showValidation && !!fieldErrors.amountInDispute}
                        />
                        <Form.Control.Feedback type="invalid">
                            {fieldErrors.amountInDispute}
                        </Form.Control.Feedback>
                    </Form.Group>
                </Row>

                <Form.Group controlId="disputeDescription" className="mb-3">
                    <Form.Label className="fw-semibold fs-6 mb-1">
                        Describe your dispute in Details <span className="text-danger">*</span>
                        <OverlayTrigger
                            placement="right"
                            overlay={<Tooltip id="button-tooltip-2">Enter detailed description</Tooltip>}
                        >
                            {({ ref, ...triggerHandler }) => (
                                <Button
                                    variant="light"
                                    {...triggerHandler}
                                    className="d-inline-flex align-items-center"
                                    style={{ backgroundColor: "transparent", border: "none" }}
                                >
                                    <Image
                                        ref={ref}
                                        roundedCircle
                                        src={infoImg}
                                        style={{ height: "3vh" }}
                                        alt=""
                                    />
                                </Button>
                            )}
                        </OverlayTrigger>
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        onChange={handleInputChange}
                        value={formData.disputeDescription}
                        name="disputeDescription"
                        maxLength={1000}
                        required
                        placeholder="Enter detailed description of your dispute"
                        isInvalid={showValidation && !!fieldErrors.disputeDescription}
                    />
                    <Form.Control.Feedback type="invalid">
                        {fieldErrors.disputeDescription}
                    </Form.Control.Feedback>
                </Form.Group>

                <div className="border-3 rounded px-4 py-3 mt-3" style={{ background: "#F4F4F9" }}>
                    <h4>Details of Respondent</h4>
                    <hr />
                    <Row className="mb-3">
                        <Form.Group as={Col} controlId="respondentCompanyName">
                            <Form.Label className="fw-semibold fs-6 mb-1">
                                Name of Company <span className="text-danger">*</span>
                                <OverlayTrigger
                                    placement="right"
                                    overlay={<Tooltip id="button-tooltip-2">Enter company name</Tooltip>}
                                >
                                    {({ ref, ...triggerHandler }) => (
                                        <Button
                                            variant="light"
                                            {...triggerHandler}
                                            className="d-inline-flex align-items-center"
                                            style={{ backgroundColor: "transparent", border: "none" }}
                                        >
                                            <Image
                                                ref={ref}
                                                roundedCircle
                                                src={infoImg}
                                                style={{ height: "3vh" }}
                                                alt=""
                                            />
                                        </Button>
                                    )}
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                onChange={handleInputChange}
                                value={formData.respondentCompanyName}
                                name="respondentCompanyName"
                                maxLength={100}
                                required
                                placeholder="Enter company name"
                                isInvalid={showValidation && !!fieldErrors.respondentCompanyName}
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.respondentCompanyName}
                            </Form.Control.Feedback>
                        </Form.Group>

                    
                    </Row>

                    <Row className="mb-3">
                        <Form.Group as={Col} controlId="respondentRepresentativeName">
                            <Form.Label className="fw-semibold fs-6 mb-1">
                                Name of Representative <span className="text-danger">*</span>
                                <OverlayTrigger
                                    placement="right"
                                    overlay={<Tooltip id="button-tooltip-2">Enter representative name</Tooltip>}
                                >
                                    {({ ref, ...triggerHandler }) => (
                                        <Button
                                            variant="light"
                                            {...triggerHandler}
                                            className="d-inline-flex align-items-center"
                                            style={{ backgroundColor: "transparent", border: "none" }}
                                        >
                                            <Image
                                                ref={ref}
                                                roundedCircle
                                                src={infoImg}
                                                style={{ height: "3vh" }}
                                                alt=""
                                            />
                                        </Button>
                                    )}
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                onChange={handleInputChange}
                                value={formData.respondentRepresentativeName || formData.respondentName || ""}
                                name="respondentRepresentativeName"
                                maxLength={100}
                                required
                                placeholder="Enter representative name"
                                isInvalid={showValidation && !!fieldErrors.respondentRepresentativeName}
                                disabled
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.respondentRepresentativeName}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group as={Col} controlId="respondentEmail">
                            <Form.Label className="fw-semibold fs-6 mb-1">
                                Email <span className="text-danger">*</span>
                                <OverlayTrigger
                                    placement="right"
                                    overlay={<Tooltip id="button-tooltip-2">Email id of Respondent</Tooltip>}
                                >
                                    {({ ref, ...triggerHandler }) => (
                                        <Button
                                            variant="light"
                                            {...triggerHandler}
                                            className="d-inline-flex align-items-center"
                                            style={{ backgroundColor: "transparent", border: "none" }}
                                        >
                                            <Image
                                                ref={ref}
                                                roundedCircle
                                                src={infoImg}
                                                style={{ height: "3vh" }}
                                                alt=""
                                            />
                                        </Button>
                                    )}
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                onChange={handleInputChange}
                                value={formData.respondentEmail}
                                name="respondentEmail"
                                maxLength={200}
                                required
                                placeholder="Enter Respondent Email"
                                isInvalid={showValidation && !!fieldErrors.respondentEmail}
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.respondentEmail}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Row>

                    <Row className="mb-3">
                        <Form.Group as={Col} controlId="respondentAddress">
                            <Form.Label className="fw-semibold fs-6 mb-1">
                                Address <span className="text-danger">*</span>
                                <OverlayTrigger
                                    placement="right"
                                    overlay={<Tooltip id="button-tooltip-2">Enter address</Tooltip>}
                                >
                                    {({ ref, ...triggerHandler }) => (
                                        <Button
                                            variant="light"
                                            {...triggerHandler}
                                            className="d-inline-flex align-items-center"
                                            style={{ backgroundColor: "transparent", border: "none" }}
                                        >
                                            <Image
                                                ref={ref}
                                                roundedCircle
                                                src={infoImg}
                                                style={{ height: "3vh" }}
                                                alt=""
                                            />
                                        </Button>
                                    )}
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                onChange={handleInputChange}
                                value={formData.respondentAddress}
                                name="respondentAddress"
                                maxLength={200}
                                required
                                placeholder="Enter address"
                                isInvalid={showValidation && !!fieldErrors.respondentAddress}
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.respondentAddress}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Row>

                    <Row className="mb-3">
                        <Form.Group as={Col} controlId="respondentCountry">
                            <Form.Label className="fw-semibold fs-6 mb-1">
                                Country <span className="text-danger">*</span>
                                <OverlayTrigger
                                    placement="right"
                                    overlay={<Tooltip id="button-tooltip-2">Select country</Tooltip>}
                                >
                                    {({ ref, ...triggerHandler }) => (
                                        <Button
                                            variant="light"
                                            {...triggerHandler}
                                            className="d-inline-flex align-items-center"
                                            style={{ backgroundColor: "transparent", border: "none" }}
                                        >
                                            <Image
                                                ref={ref}
                                                roundedCircle
                                                src={infoImg}
                                                style={{ height: "3vh" }}
                                                alt=""
                                            />
                                        </Button>
                                    )}
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Select
                                value={formData.respondentCountry}
                                onChange={handleInputChange}
                                name="respondentCountry"
                                required
                                isInvalid={showValidation && !!fieldErrors.respondentCountry}
                            >
                                <option value="">Select Country</option>
                                {countriesList.map((value, index) => (
                                    <option key={index} value={value}>
                                        {value}
                                    </option>
                                ))}
                            </Form.Select>
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.respondentCountry}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group as={Col} controlId="respondentState">
                            <Form.Label className="fw-semibold fs-6 mb-1">
                                State <span className="text-danger">*</span>
                                <OverlayTrigger
                                    placement="right"
                                    overlay={<Tooltip id="button-tooltip-2">Enter state</Tooltip>}
                                >
                                    {({ ref, ...triggerHandler }) => (
                                        <Button
                                            variant="light"
                                            {...triggerHandler}
                                            className="d-inline-flex align-items-center"
                                            style={{ backgroundColor: "transparent", border: "none" }}
                                        >
                                            <Image
                                                ref={ref}
                                                roundedCircle
                                                src={infoImg}
                                                style={{ height: "3vh" }}
                                                alt=""
                                            />
                                        </Button>
                                    )}
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                onChange={handleInputChange}
                                value={formData.respondentState}
                                name="respondentState"
                                maxLength={50}
                                required
                                placeholder="Enter state"
                                isInvalid={showValidation && !!fieldErrors.respondentState}
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.respondentState}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group as={Col} controlId="respondentZipCode">
                            <Form.Label className="fw-semibold fs-6 mb-1">
                                Zip Code
                                <OverlayTrigger
                                    placement="right"
                                    overlay={<Tooltip id="button-tooltip-2">Enter zip code</Tooltip>}
                                >
                                    {({ ref, ...triggerHandler }) => (
                                        <Button
                                            variant="light"
                                            {...triggerHandler}
                                            className="d-inline-flex align-items-center"
                                            style={{ backgroundColor: "transparent", border: "none" }}
                                        >
                                            <Image
                                                ref={ref}
                                                roundedCircle
                                                src={infoImg}
                                                style={{ height: "3vh" }}
                                                alt=""
                                            />
                                        </Button>
                                    )}
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                onChange={(e) => {
                                    const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 6);
                                    handleInputChange({ target: { name: "respondentZipCode", value } });
                                }}
                                value={formData.respondentZipCode}
                                name="respondentZipCode"
                                minLength={6}
                                maxLength={12}
                                required
                                placeholder="Enter zip code"
                                isInvalid={showValidation && !!fieldErrors.respondentZipCode}
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.respondentZipCode}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Row>

                    <Row className="mb-3">
                        <Form.Group as={Col} controlId="respondentPanNo">
                            <Form.Label className="fw-semibold fs-6 mb-1">
                                PAN No (Optional)
                                <OverlayTrigger
                                    placement="right"
                                    overlay={<Tooltip id="button-tooltip-2">Enter PAN number</Tooltip>}
                                >
                                    {({ ref, ...triggerHandler }) => (
                                        <Button
                                            variant="light"
                                            {...triggerHandler}
                                            className="d-inline-flex align-items-center"
                                            style={{ backgroundColor: "transparent", border: "none" }}
                                        >
                                            <Image
                                                ref={ref}
                                                roundedCircle
                                                src={infoImg}
                                                style={{ height: "3vh" }}
                                                alt=""
                                            />
                                        </Button>
                                    )}
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                onChange={(e) => {
                                    const value = e.target.value.toUpperCase();
                                    handleInputChange({ target: { name: "respondentPanNo", value } });
                                }}
                                value={formData.respondentPanNo}
                                name="respondentPanNo"
                                maxLength={10}
                                placeholder="ABCDE1234F (optional)"
                                isInvalid={showValidation && !!fieldErrors.respondentPanNo}
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.respondentPanNo}
                            </Form.Control.Feedback>
                        </Form.Group>

                        <Form.Group as={Col} controlId="respondentWebsite">
                            <Form.Label className="fw-semibold fs-6 mb-1">
                                Website (Optional)
                                <OverlayTrigger
                                    placement="right"
                                    overlay={<Tooltip id="button-tooltip-2">Enter website URL</Tooltip>}
                                >
                                    {({ ref, ...triggerHandler }) => (
                                        <Button
                                            variant="light"
                                            {...triggerHandler}
                                            className="d-inline-flex align-items-center"
                                            style={{ backgroundColor: "transparent", border: "none" }}
                                        >
                                            <Image
                                                ref={ref}
                                                roundedCircle
                                                src={infoImg}
                                                style={{ height: "3vh" }}
                                                alt=""
                                            />
                                        </Button>
                                    )}
                                </OverlayTrigger>
                            </Form.Label>
                            <Form.Control
                                type="text"
                                onChange={handleInputChange}
                                value={formData.respondentWebsite}
                                name="respondentWebsite"
                                maxLength={100}
                                placeholder="Enter website URL (optional)"
                                isInvalid={showValidation && !!fieldErrors.respondentWebsite}
                            />
                            <Form.Control.Feedback type="invalid">
                                {fieldErrors.respondentWebsite}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Row>

                    <Form.Group className="mb-3 mt-3" id="formGridCheckbox">
                        <div className="d-flex align-items-start gap-2">
                            <input
                                type="checkbox"
                                name="termsAccepted"
                                checked={formData.termsAccepted || false}
                                onChange={(e) =>
                                    setFormData((prev) => ({ ...prev, termsAccepted: e.target.checked }))
                                }
                                style={{ width: "16px", height: "16px", marginTop: "2px" }}
                                required
                            />
                            <div>
                                <label className="mb-0" style={{ fontSize: "14px", lineHeight: "1.4" }}>
                                    I hereby affirm that all the information provided herein is accurate and true
                                    to the best of my knowledge and I shall adhere to all the regulations of Asian
                                    Exporters Chamber of Commerce and Industry.
                                </label>
                                {showValidation && !formData.termsAccepted && (
                                    <div className="text-danger mt-1 small">
                                        You must accept the terms to proceed
                                    </div>
                                )}
                            </div>
                        </div>
                    </Form.Group>
                </div>

                {!isFormValid && (
                    <div className="text-danger mt-3">
                        Please fill all required fields correctly before proceeding to the next step.
                    </div>
                )}
            </div>
        </>
    );
};

export default DetailsOfDispute;